import { styled } from "@mui/system";
import ReactDOMServer from "react-dom/server";
import { QRCode } from "react-qrcode-logo";
import CnbLogoMobile from "src/icons/CnbLogoMobile";
import { useUserSlice } from "src/store/features/user.slice";

interface QRWrapperProps {
  background: string;
  size: string;
  isUnverified: boolean;
  hide: boolean;
}
const QRWrapper = styled("div")<QRWrapperProps>(({ background, size, isUnverified, hide }) => ({
  display: hide ? "none" : "flex",
  width: isUnverified ? "100%" : size,
  maxWidth: isUnverified ? "100%" : size,
  height: size,
  maxHeight: isUnverified ? "100px" : "",
  padding: "16px",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: "4px",
  flexShrink: 0,
  borderRadius: "8px",
  background: background,
  zIndex: 1,
  visibility: hide ? "hidden" : "visible",
}));

const UnverifiedText = styled("div")(() => ({
  display: "flex",
  width: "100%",
  justifyContent: "center",
  alignItems: "center",
  gap: "16px",
  color: "#002035",
  textAlign: "center",
  flex: "1 0 0",
  fontFamily: "Söhne",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "17px",
}));

interface QRCodeBadgeProps {
  tier: "bronze" | "silver" | "gold" | "black";
  backgroundSize: string;
  qrSize: number;
}

/**
 * A QR code badge component to display QR codes with different tiers.
 * @param {("bronze" | "silver" | "gold" | "black")} props.tier - The tier of the QR code badge.
 * @param {string} props.backgroundSize - The size of the background of the badge.
 * @param {string} props.qrSize - The size of the QR code image.
 */
const QRCodeBadge = (props: QRCodeBadgeProps) => {
  const { tier, backgroundSize, qrSize } = props;

  const {
    raf: { rafCode, rafOptedIn },
    isUnverified,
  } = useUserSlice();

  let qrBackground = "";
  switch (tier) {
    case "bronze":
      qrBackground = "#D1CEC9";
      break;
    case "silver":
      qrBackground = "#F5F6F7";
      break;
    case "gold":
      qrBackground = "#F2ECD5";
      break;
    case "black":
      qrBackground = "#EAE9E6";
      break;
    default:
      break;
  }

  const svgString = ReactDOMServer.renderToString(
    <CnbLogoMobile style={{ background: "#002035" }} />,
  );
  const dataURL = "data:image/svg+xml;base64," + btoa(svgString);

  return (
    <QRWrapper
      id="RaF-QRCodeBadge"
      background={"#002035"}
      size={backgroundSize}
      isUnverified={isUnverified}
      hide={!rafOptedIn}>
      {isUnverified ? (
        <UnverifiedText>Verify to view code</UnverifiedText>
      ) : (
        <QRCode
          value={`app.calebandbrown.com/signup?referrer=${rafCode}`}
          size={qrSize}
          bgColor={"#002035"}
          fgColor={qrBackground}
          logoImage={dataURL}
          removeQrCodeBehindLogo={true}
          logoPaddingStyle="square"
          logoWidth={qrSize * 0.22}
          logoHeight={qrSize * 0.22}
          logoOpacity={1}
          qrStyle="squares"
          eyeColor={{
            outer: qrBackground,
            inner: "#FFF",
          }}
          quietZone={8}
          ecLevel={"M"}
          enableCORS={true}
          logoPadding={4}
        />
      )}
    </QRWrapper>
  );
};

export default QRCodeBadge;
